<template>
  <LayoutCard title="License Email Notifications">
    <template #description>
      Configure the emails applicants receive when a license enters a workflow status.
      Each rule has its own trigger, delivery behavior, and message template.
    </template>

    <template #footer v-if="changed">
      <button type="button" class="btn btn-flat ms-1" :disabled="saving" @click="reset">
        Cancel
      </button>
      <button
        type="button"
        class="btn btn-primary ms-1"
        :disabled="saving || uploadingRuleIds.length > 0 || clientValidationErrors.length > 0"
        @click="commit"
      >
        <span v-if="saving" class="spinner-border spinner-border-sm me-1" aria-hidden="true" />
        Save Changes
      </button>
    </template>

    <div v-if="isLegacyConfiguration" class="alert alert-info mb-0" role="status">
      The existing approval email has been converted into a rule below. Saving these settings
      completes the migration to configurable license email notifications.
    </div>

    <div v-if="validationErrors.length" class="alert alert-danger mb-0" role="alert">
      <div class="fw-semibold mb-1">Resolve these issues before saving:</div>
      <ul class="mb-0 ps-3">
        <li v-for="error in validationErrors" :key="error">{{ readableValidationError(error) }}</li>
      </ul>
    </div>

    <div v-if="rules.length" class="d-flex flex-column gap-3">
      <section
        v-for="(rule, index) in rules"
        :key="rule.id"
        :class="[
          'notification-rule border rounded overflow-hidden',
          { 'notification-rule--expanded': isRuleExpanded(rule) }
        ]"
      >
        <div class="notification-rule-header d-flex flex-row align-items-center gap-3">
          <button
            type="button"
            class="notification-rule-title-button d-flex flex-row flex-wrap align-items-baseline gap-2 text-start flex-grow-1"
            :aria-expanded="isRuleExpanded(rule)"
            @click="toggleRuleExpanded(rule)"
          >
            <span class="fw-semibold">{{ rule.name || `Notification ${index + 1}` }}</span>
            <span class="small text-muted">{{ summarizeRule(rule) }}</span>
          </button>
          <div class="d-flex flex-row align-items-center gap-1 ms-auto">
            <button
              type="button"
              class="btn btn-flat btn-sm text-danger"
              :disabled="saving"
              :aria-label="`Remove ${rule.name || `notification ${index + 1}`}`"
              @click="removeRule(index)"
            >
              <i class="bi-trash" aria-hidden="true" />
            </button>
            <button
              type="button"
              class="btn btn-flat btn-sm"
              :aria-label="isRuleExpanded(rule) ? 'Collapse notification' : 'Expand notification'"
              :aria-expanded="isRuleExpanded(rule)"
              @click="toggleRuleExpanded(rule)"
            >
              <i :class="isRuleExpanded(rule) ? 'bi-chevron-up' : 'bi-chevron-down'" aria-hidden="true" />
            </button>
          </div>
        </div>

        <Transition
          name="notification-rule-collapse"
          @before-enter="beforeRuleBodyEnter"
          @enter="enterRuleBody"
          @after-enter="afterRuleBodyTransition"
          @before-leave="beforeRuleBodyLeave"
          @leave="leaveRuleBody"
          @after-leave="afterRuleBodyTransition"
        >
          <div v-if="isRuleExpanded(rule)" class="notification-rule-collapse-panel">
            <div class="notification-rule-body d-flex flex-column gap-4 p-3">
              <div class="notification-field">
                <label class="form-label fw-semibold mb-1" :for="fieldId(rule, 'name')">Name</label>
                <div class="small text-muted mb-2">
                  A short staff-facing name shown in confirmations and delivery history.
                </div>
                <input
                  :id="fieldId(rule, 'name')"
                  v-model="rule.name"
                  class="form-control"
                  :disabled="saving"
                  placeholder="License active"
                />
              </div>

              <fieldset class="notification-field">
                <label class="form-label fw-semibold mb-1">Delivery</label>
                <div class="small text-muted mb-2">
                  Choose whether this notification is inactive, staff-confirmed, or queued automatically.
                </div>
                <div class="radio-card-group" role="radiogroup">
                  <label
                    v-for="option in deliveryOptions"
                    :key="option.value"
                    :class="['radio-card', { 'radio-card--selected': rule.mode === option.value }]"
                  >
                    <input
                      v-model="rule.mode"
                      class="form-check-input mt-1"
                      type="radio"
                      :name="fieldId(rule, 'mode')"
                      :value="option.value"
                      :disabled="saving"
                    />
                    <span>
                      <span class="d-block fw-semibold">{{ option.label }}</span>
                      <span class="d-block small text-muted">{{ option.description }}</span>
                    </span>
                  </label>
                </div>
              </fieldset>

              <fieldset class="notification-field">
                <label class="form-label fw-semibold mb-1">Trigger status</label>
                <div class="small text-muted mb-2">
                  Send this notification when a license changes into one of these configured statuses.
                </div>
                <div class="radio-card-group" role="radiogroup">
                  <label
                    v-for="status in statusOptionsFor(rule)"
                    :key="statusOptionCode(status)"
                    :class="[
                      'radio-card',
                      { 'radio-card--selected': rule.trigger.statusCode === statusOptionCode(status) }
                    ]"
                  >
                    <input
                      v-model="rule.trigger.statusCode"
                      class="form-check-input mt-1"
                      type="radio"
                      :name="fieldId(rule, 'status-code')"
                      :value="statusOptionCode(status)"
                      :disabled="saving"
                    />
                    <span>
                      <span class="d-block fw-semibold">{{ status.label }}</span>
                      <span class="d-block small text-muted">{{ statusDescription(status) }}</span>
                    </span>
                  </label>
                </div>
              </fieldset>

              <fieldset class="notification-field">
                <label class="form-label fw-semibold mb-1">Repeat behavior</label>
                <div class="small text-muted mb-2">
                  Control whether to send once per license, once per term, or on every entry.
                </div>
                <div class="radio-card-group" role="radiogroup">
                  <label
                    v-for="option in repeatPolicyOptions"
                    :key="option.value"
                    :class="['radio-card', { 'radio-card--selected': rule.repeatPolicy === option.value }]"
                  >
                    <input
                      v-model="rule.repeatPolicy"
                      class="form-check-input mt-1"
                      type="radio"
                      :name="fieldId(rule, 'repeat-policy')"
                      :value="option.value"
                      :disabled="saving"
                    />
                    <span>
                      <span class="d-block fw-semibold">{{ option.label }}</span>
                      <span class="d-block small text-muted">{{ option.description }}</span>
                    </span>
                  </label>
                </div>
              </fieldset>

              <fieldset class="notification-field">
                <label class="form-check">
                  <input class="form-check-input" type="checkbox" :checked="rule.attachPrintableLicense" :disabled="saving || !hasActiveTrigger(rule)" @change="setAttachPrintableLicense(rule, $event.target.checked)" />
                  <span class="form-check-label fw-semibold">Attach printable license (PDF)</span>
                </label>
                <p class="small text-muted">A one-page Letter certificate for guests. Available for active statuses.</p>
                <template v-if="rule.attachPrintableLicense">
                  <button type="button" class="btn btn-outline-secondary btn-sm edit-printable-license" :disabled="saving" @click="showPrintableLicenseModal = true">Edit printable license</button>
                  <p class="small text-muted mt-2">The printable license design is shared across this municipality's notifications.</p>
                  <label class="form-check">
                    <input class="form-check-input" type="checkbox" :checked="rule.sendOnRenewal" :disabled="saving" @change="setSendOnRenewal(rule, $event.target.checked)" />
                    <span class="form-check-label">Send a replacement when an active license is renewed</span>
                  </label>
                </template>
              </fieldset>

              <div class="notification-field">
                <label class="form-label fw-semibold mb-1" :for="fieldId(rule, 'format')">Email body style</label>
                <select
                  :id="fieldId(rule, 'format')"
                  class="form-select"
                  :value="rule.template.format || 'standard'"
                  :disabled="saving || uploadingRuleIds.includes(rule.id)"
                  @change="setBodyFormat(rule, $event.target.value)"
                >
                  <option value="standard">Standard notification</option>
                  <option value="markdown">Fully custom body (Markdown)</option>
                </select>
              </div>

              <div class="notification-field">
                <label class="form-label fw-semibold mb-1" :for="fieldId(rule, 'subject')">Subject</label>
                <div class="small text-muted mb-2">
                  The subject line applicants see in their inbox.
                </div>
                <input
                  :id="fieldId(rule, 'subject')"
                  v-model="rule.template.subject"
                  class="form-control"
                  :maxlength="subjectMaxLength"
                  :disabled="saving"
                  placeholder="Your license is active"
                />
              </div>
              <div class="notification-field">
                <label class="form-label fw-semibold mb-1" :for="fieldId(rule, 'message')">Body</label>
                <div v-if="rule.template.format === 'markdown'" class="small text-muted mb-2">
                  Design the full email, including your banner, registration details, and permit wording.
                  Include any greeting, contact information, or signature you want recipients to see.
                </div>
                <div v-else class="small text-muted mb-2">
                  The plain-language message inserted into the applicant email.
                </div>
                <MarkdownEmailEditor
                  v-if="rule.template.format === 'markdown'"
                  :id="fieldId(rule, 'message')"
                  :model-value="rule.template.message || ''"
                  :city-id="city.id"
                  :images="rule.template.images"
                  :disabled="saving"
                  @update:model-value="rule.template.message = $event"
                  @update:images="rule.template.images = $event"
                  @busy="setImageUploadBusy(rule.id, $event)"
                />
                <textarea
                  v-else
                  :id="fieldId(rule, 'message')"
                  v-model="rule.template.message"
                  class="form-control"
                  rows="5"
                  :maxlength="messageMaxLength"
                  :disabled="saving"
                  placeholder="License {{licenseId}} is now {{statusLabel}}."
                />
              </div>

              <div class="d-flex flex-row flex-wrap align-items-center gap-1 small text-muted">
                <span>Template fields:</span>
                <code
                  v-for="token in tokensForRule(rule)"
                  :key="token"
                  class="template-token-label"
                >
                  {{ token }}
                </code>
              </div>

              <div class="d-flex flex-row justify-content-end">
                <button
                  type="button"
                  class="btn btn-outline-secondary"
                  :disabled="saving || ruleImageUploadBusy(rule) || previewingRuleId === rule.id || ruleHasClientValidationErrors(rule)"
                  @click="openRulePreview(rule)"
                >
                  <span
                    v-if="previewingRuleId === rule.id"
                    class="spinner-border spinner-border-sm me-1"
                    aria-hidden="true"
                  />
                  Preview email
                </button>
              </div>
            </div>
          </div>
        </Transition>
      </section>
    </div>

    <div v-else class="border rounded p-4 text-center text-muted">
      No applicant email notifications are configured.
    </div>

    <div class="d-flex flex-row flex-wrap align-items-center justify-content-between gap-2">
      <button type="button" class="btn btn-outline-primary" :disabled="saving" @click="addRule">
        <i class="bi-plus-lg me-1" /> Add notification
      </button>
    </div>
  </LayoutCard>

  <ModalView
    v-model="showPreviewModal"
    :title="`${previewRule?.name || 'Notification'} Preview`"
    size="lg"
    scrollable
    hide-ok
    cancel-title="Close"
  >
    <LoadingMessage v-if="previewLoading" message="Loading email preview" />
    <div v-else-if="previewError" class="alert alert-danger mb-0" role="alert">
      <div>{{ previewError.message || String(previewError) }}</div>
      <button type="button" class="btn btn-outline-danger btn-sm mt-2" @click="reloadPreview">Try again</button>
    </div>
    <div v-else-if="preview" class="d-flex flex-column gap-3">
      <EmailPdfAttachments :attachments="preview.attachments" />
      <div class="small text-muted">
        Previewed with sample applicant data: John Smith, LIC-001, 1 Main St.
      </div>
      <div>
        <div class="form-label">Subject</div>
        <div class="border rounded bg-light px-3 py-2">{{ preview.subject }}</div>
      </div>
      <iframe
        class="status-email-preview-frame"
        title="Styled draft license email preview"
        sandbox
        :srcdoc="preview.html"
      />
    </div>
  </ModalView>
  <PrintableLicenseSettingsModal v-model="showPrintableLicenseModal" :city="city" @saved="onPrintableLicenseSaved" />
</template>

<script>
import LayoutCard from '@/components/common/LayoutCard.vue';
import LoadingMessage from '@/components/common/LoadingMessage.vue';
import MarkdownEmailEditor from '@/components/common/MarkdownEmailEditor.vue';
import ModalView from '@/components/common/ModalView.vue';
import { City } from '@/models/City';
import PrintableLicenseSettingsModal from '@/components/licenses/PrintableLicenseSettingsModal.vue';
import EmailPdfAttachments from '@/components/licenses/EmailPdfAttachments.vue';
import {
  LICENSE_STATUS_EMAIL_SUBJECT_MAX_LENGTH,
  LICENSE_STATUS_EMAIL_TEMPLATE_TEXT_MAX_LENGTH,
  LICENSE_STATUS_EMAIL_FIELDS,
  normalizeLicenseStatusEmailRule,
  validateLicenseStatusEmails
} from '@strenforcement/domain/licenseStatusEmail';

const TEMPLATE_TOKENS = Object.freeze([
  '{{licenseId}}',
  '{{address}}',
  '{{statusLabel}}',
  '{{previousStatusLabel}}',
  '{{issuedOn}}',
  '{{expiresOn}}'
]);

const DELIVERY_OPTIONS = Object.freeze([
  {
    value: 'disabled',
    label: 'Disabled',
    description: 'Do not send this notification.'
  },
  {
    value: 'confirm_before_sending',
    label: 'Ask staff',
    description: 'Prompt staff before queueing.'
  },
  {
    value: 'send_automatically',
    label: 'Automatic',
    description: 'Queue as soon as the status changes.'
  }
]);

const REPEAT_POLICY_OPTIONS = Object.freeze([
  {
    value: 'once_per_term',
    label: 'Once per term',
    description: 'Send once for each expiration date, including enabled renewal notifications.'
  },
  {
    value: 'once_per_license',
    label: 'Once per license',
    description: 'Do not send again after it has been handled.'
  },
  {
    value: 'every_entry',
    label: 'Every entry',
    description: 'Send each time the status is entered.'
  }
]);

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

export default {
  components: {
    PrintableLicenseSettingsModal,
    EmailPdfAttachments,
    LayoutCard,
    LoadingMessage,
    ModalView,
    MarkdownEmailEditor
  },
  props: {
    city: City
  },
  data() {
    return {
      rules: [],
      savedRulesJson: '[]',
      saving: false,
      showPrintableLicenseModal: false,
      uploadingRuleIds: [],
      saveError: null,
      templateTokens: TEMPLATE_TOKENS,
      deliveryOptions: DELIVERY_OPTIONS,
      repeatPolicyOptions: REPEAT_POLICY_OPTIONS,
      subjectMaxLength: LICENSE_STATUS_EMAIL_SUBJECT_MAX_LENGTH,
      messageMaxLength: LICENSE_STATUS_EMAIL_TEMPLATE_TEXT_MAX_LENGTH,
      expandedRuleIds: [],
      showPreviewModal: false,
      preview: null,
      previewError: null,
      previewLoading: false,
      previewRule: null,
      previewingRuleId: null,
      previewLoadToken: 0
    };
  },
  watch: {
    city: {
      immediate: true,
      handler(next, previous) {
        if (next?.id !== previous?.id) {
          this.showPrintableLicenseModal = false;
          this.reset();
        } else if (!this.saving && !this.changed) this.reset();
      }
    }
  },
  computed: {
    isLegacyConfiguration() {
      return this.city?.hasStoredLicenseStatusEmails === false;
    },
    rulesJson() {
      return JSON.stringify(this.rules);
    },
    changed() {
      return this.rulesJson !== this.savedRulesJson;
    },
    validationErrors() {
      return [
        ...(this.saveError ? [this.saveError] : []),
        ...this.clientValidationErrors
      ];
    },
    clientValidationErrors() {
      return validateLicenseStatusEmails({ rules: this.rules }, {
        cityId: this.city?.id,
        statusConfig: this.city?.licenseStatuses || []
      });
    }
  },
  methods: {
    hasActiveTrigger(rule) {
      return this.city.licenseStatuses.some(status => this.statusOptionCode(status) === rule.trigger.statusCode && status.type === 'active');
    },
    setAttachPrintableLicense(rule, enabled) {
      rule.attachPrintableLicense = enabled;
      this.setSendOnRenewal(rule, enabled);
    },
    setSendOnRenewal(rule, enabled) {
      rule.sendOnRenewal = enabled;
      if (enabled) rule.repeatPolicy = 'once_per_term';
    },
    onPrintableLicenseSaved() {
      this.closePreview();
      this.$showToast('Printable license saved for this municipality');
    },
    /** Changes body authoring without replacing the staff's existing text. */
    setBodyFormat(rule, format) {
      if (format === 'standard') delete rule.template.format;
      else rule.template.format = format;
    },

    /** Shows the fields supported by the selected body format. */
    tokensForRule(rule) {
      return rule.template.format === 'markdown'
        ? LICENSE_STATUS_EMAIL_FIELDS.map(field => `{{${field.name}}}`)
        : this.templateTokens;
    },

    /** Prevents saving or previewing before an image has finished uploading. */
    setImageUploadBusy(ruleId, busy) {
      this.uploadingRuleIds = this.uploadingRuleIds.filter(id => id !== ruleId);
      if (busy) this.uploadingRuleIds.push(ruleId);
    },
    ruleImageUploadBusy(rule) {
      return this.uploadingRuleIds.includes(rule.id);
    },

    fieldId(rule, field) {
      return `license-status-email-${rule.id}-${field}`;
    },
    statusOptionCode(status) {
      return String(status?.code || status?.id || '').trim();
    },
    statusOptionsFor(rule) {
      const statuses = Array.isArray(this.city?.licenseStatuses) ? this.city.licenseStatuses : [];
      const currentCode = rule.trigger?.statusCode;
      if (currentCode && !statuses.some(status => this.statusOptionCode(status) === currentCode)) {
        return [{ code: currentCode, label: `${currentCode} — no longer configured`, type: 'unavailable' }, ...statuses];
      }
      return statuses;
    },
    statusDescription(status) {
      if (status.type === 'unavailable') {
        return 'This status is no longer configured.';
      }
      return `${status.type || 'Workflow'} status`;
    },
    ruleHasClientValidationErrors(rule) {
      return validateLicenseStatusEmails({ rules: [rule] }, {
        cityId: this.city?.id,
        statusConfig: this.city?.licenseStatuses || []
      }).length > 0;
    },
    isRuleExpanded(rule) {
      return this.expandedRuleIds.includes(rule.id);
    },
    toggleRuleExpanded(rule) {
      if (this.isRuleExpanded(rule)) {
        this.expandedRuleIds = this.expandedRuleIds.filter(id => id !== rule.id);
      } else {
        this.expandedRuleIds = [...this.expandedRuleIds, rule.id];
      }
    },
    nextFrame(callback) {
      const schedule = typeof window !== 'undefined' && window.requestAnimationFrame
        ? window.requestAnimationFrame
        : setTimeout;
      schedule(callback);
    },
    beforeRuleBodyEnter(element) {
      element.style.height = '0';
      element.style.opacity = '0';
    },
    enterRuleBody(element) {
      this.nextFrame(() => {
        element.style.height = `${element.scrollHeight}px`;
        element.style.opacity = '1';
      });
    },
    beforeRuleBodyLeave(element) {
      element.style.height = `${element.scrollHeight}px`;
      element.style.opacity = '1';
    },
    leaveRuleBody(element) {
      this.nextFrame(() => {
        element.style.height = '0';
        element.style.opacity = '0';
      });
    },
    afterRuleBodyTransition(element) {
      element.style.height = '';
      element.style.opacity = '';
    },
    nextRuleId() {
      const ids = new Set(this.rules.map(rule => rule.id));
      let index = 1;
      while (ids.has(`status-email-${index}`)) index += 1;
      return `status-email-${index}`;
    },
    addRule() {
      const firstStatus = this.city?.licenseStatuses?.[0]?.code || '';
      const id = this.nextRuleId();
      this.rules.push({
        id,
        name: 'New notification',
        trigger: { type: 'status_entered', statusCode: firstStatus },
        mode: 'disabled',
        repeatPolicy: 'once_per_license',
        template: {
          subject: 'License {{licenseId}} status update',
          message: 'Your license is now {{statusLabel}}.'
        }
      });
      this.expandedRuleIds = [...this.expandedRuleIds, id];
      this.saveError = null;
    },
    removeRule(index) {
      const [rule] = this.rules.slice(index, index + 1);
      this.rules.splice(index, 1);
      if (rule) {
        this.expandedRuleIds = this.expandedRuleIds.filter(id => id !== rule.id);
        if (this.previewRule?.id === rule.id) {
          this.closePreview();
        }
      }
      this.saveError = null;
    },
    summarizeRule(rule) {
      const delivery = {
        disabled: 'Disabled',
        confirm_before_sending: 'Staff confirmation',
        send_automatically: 'Automatic'
      }[rule.mode] || rule.mode;
      const status = this.statusOptionsFor(rule).find(option => this.statusOptionCode(option) === rule.trigger?.statusCode);
      return `${delivery} · enters ${status?.label || rule.trigger?.statusCode || 'an unselected status'}`;
    },
    readableValidationError(error) {
      return String(error)
        .replace(/^licenseStatusEmails\.rules\[(\d+)]/, (_match, index) => (
          `Notification ${Number(index) + 1}`
        ))
        .replace(/\.template\./, ' ')
        .replace(/\.trigger\./, ' ')
        .replace(/\.repeatPolicy/, ' repeat behavior')
        .replace(/\.mode/, ' delivery mode')
        .replace(/\.name/, ' name')
        .replace(/\.id/, ' identifier');
    },
    reset() {
      const rules = Array.isArray(this.city?.licenseStatusEmailRules)
        ? this.city.licenseStatusEmailRules
        : [];
      this.rules = clone(rules.map(rule => normalizeLicenseStatusEmailRule(rule)).filter(Boolean));
      this.expandedRuleIds = [];
      this.savedRulesJson = JSON.stringify(this.rules);
      this.saveError = null;
      this.closePreview();
    },
    async commit() {
      this.saveError = null;
      if (this.clientValidationErrors.length || this.uploadingRuleIds.length) return;

      const normalizedRules = this.rules.map(rule => normalizeLicenseStatusEmailRule(rule));
      this.saving = true;
      try {
        await this.city.updateLicenseStatusEmails({ rules: normalizedRules });
        this.rules = clone(this.city.licenseStatusEmailRules);
        this.savedRulesJson = JSON.stringify(this.rules);
        this.$showToast('License email notifications saved');
      } catch (error) {
        this.saveError = error?.message || String(error);
      } finally {
        this.saving = false;
      }
    },
    async openRulePreview(rule) {
      if (this.ruleImageUploadBusy(rule)) return;
      const previewRule = clone(rule);
      this.showPreviewModal = true;
      this.preview = null;
      this.previewError = null;
      this.previewRule = previewRule;
      await this.loadPreview(previewRule);
    },
    async reloadPreview() {
      if (!this.previewRule) return;
      await this.loadPreview(this.previewRule);
    },
    async loadPreview(rule) {
      const token = ++this.previewLoadToken;
      this.previewingRuleId = rule.id;
      this.previewLoading = true;
      this.previewError = null;
      try {
        const preview = await this.city.previewLicenseStatusEmailDraft(rule);
        if (token === this.previewLoadToken) {
          this.preview = preview;
        }
      } catch (error) {
        if (token === this.previewLoadToken) {
          this.preview = null;
          this.previewError = error;
        }
      } finally {
        if (token === this.previewLoadToken) {
          this.previewLoading = false;
          this.previewingRuleId = null;
        }
      }
    },
    closePreview() {
      this.previewLoadToken += 1;
      this.showPreviewModal = false;
      this.preview = null;
      this.previewError = null;
      this.previewLoading = false;
      this.previewRule = null;
      this.previewingRuleId = null;
    }
  }
};
</script>

<style scoped>
.notification-rule {
  background: var(--bs-body-bg);
  transition: border-color 0.15s ease, border-width 0.15s ease;
}

.notification-rule--expanded {
  border-color: var(--bs-primary) !important;
  border-width: 2px !important;
}

.notification-rule-header {
  padding: 0.75rem 1rem;
}

.notification-rule--expanded .notification-rule-header {
  background: var(--bs-primary-bg-subtle);
}

.notification-rule-title-button {
  appearance: none;
  background: transparent;
  border: 0;
  color: inherit;
  min-width: 0;
  padding: 0;
}

.notification-rule-body {
  background: var(--bs-body-bg);
}

.notification-rule-collapse-enter-active,
.notification-rule-collapse-leave-active {
  overflow: hidden;
  transition: height 0.18s ease, opacity 0.18s ease;
}

.notification-field {
  border: 0;
  margin: 0;
  min-width: 0;
  padding: 0;
}

.radio-card-group {
  display: flex;
  flex-flow: row wrap;
  gap: 0.75rem;
}

.radio-card {
  align-items: flex-start;
  border: 1px solid var(--bs-border-color);
  border-radius: 0.5rem;
  cursor: pointer;
  display: flex;
  flex: 1 1 12rem;
  gap: 0.65rem;
  min-width: 12rem;
  padding: 0.75rem;
}

.radio-card--selected {
  background: var(--bs-primary-bg-subtle);
  border-color: var(--bs-primary);
}

.template-token-label {
  background: var(--bs-secondary-bg);
  border-radius: 1rem;
  color: var(--bs-secondary-color);
  font-family: var(--bs-font-monospace);
  padding: 0.1rem 0.35rem;
  white-space: nowrap;
}

.status-email-preview-frame {
  width: 100%;
  min-height: 34rem;
  border: 1px solid var(--bs-border-color);
  border-radius: var(--bs-border-radius);
  background: #fff;
}
</style>
